import { Flash, Send01 } from "@untitledui/icons"
import {
  type FormEvent,
  type HTMLAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"

import { Button } from "@/components/base/buttons/button"
import { TextAreaBase } from "@/components/base/textarea/textarea"
import { cx } from "@/utils/cx"

export interface SimulationMessageActionProps
  extends Omit<HTMLAttributes<HTMLFormElement>, "onSubmit"> {
  onSubmit?: (message: string) => Promise<void> | void
  onHelpRequested?: () => void
  textAreaClassName?: string
  onFocus?: () => void
  onBlur?: () => void
  onValueChange?: (value: string) => void
  // Teleprompter (Karaoke mode): when `nonce` changes, type `text` into the box
  // character-by-character (human feel), then auto-submit. Ignored while a
  // previous injection or send is in flight.
  injection?: { text: string; nonce: number }
}

export const SimulationMessageAction = ({
  className,
  onSubmit,
  onHelpRequested,
  textAreaClassName,
  onFocus,
  onBlur,
  onValueChange,
  injection,
  ...props
}: SimulationMessageActionProps) => {
  const formRef = useRef<HTMLFormElement | null>(null)
  const [value, setValue] = useState("")
  const [isComposing, setIsComposing] = useState(false)
  const [isSending, setIsSending] = useState(false)

  const doSubmit = useCallback(
    async (override?: string) => {
      const message = (override ?? value).trim()
      if (!message || isSending) return
      try {
        setIsSending(true)
        await onSubmit?.(message)
        setValue("")
        // Also reset the form to clear any native state
        formRef.current?.reset()
      } finally {
        setIsSending(false)
      }
    },
    [onSubmit, value, isSending],
  )

  // Typewriter for the teleprompter. Drains one nonce at a time; the in-flight
  // guard prevents overlap if "advance" is pressed twice quickly.
  const injectionRef = useRef<{ nonce: number; busy: boolean }>({
    nonce: 0,
    busy: false,
  })
  useEffect(() => {
    if (!injection) return
    const inj = injectionRef.current
    if (injection.nonce === inj.nonce || inj.busy || isSending) return
    inj.nonce = injection.nonce
    inj.busy = true

    const full = injection.text
    let i = 0
    let timer: number
    setValue("")
    onValueChange?.("")
    const tick = () => {
      i += 1
      const slice = full.slice(0, i)
      setValue(slice)
      onValueChange?.(slice)
      if (i < full.length) {
        timer = window.setTimeout(tick, 18 + Math.random() * 48)
      } else {
        inj.busy = false
        void doSubmit(full)
      }
    }
    timer = window.setTimeout(tick, 140)
    return () => window.clearTimeout(timer)
    // doSubmit/isSending intentionally excluded: we react only to a new nonce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [injection])

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    await doSubmit()
  }

  return (
    <form
      ref={formRef}
      className={cx(
        "bg-secondary ring-secondary relative flex h-max flex-col rounded-xl ring-1 ring-inset",
        className,
      )}
      onSubmit={handleSubmit}
      {...props}
    >
      <div className="relative flex">
        <TextAreaBase
          aria-label="Message"
          placeholder="Type your message..."
          name="message"
          value={value}
          onChange={(e) => {
            setValue(e.target.value)
            onValueChange?.(e.target.value)
          }}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={isSending}
          enterKeyHint="send"
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
          onKeyDown={(e) => {
            if (
              e.key === "Enter" &&
              !e.shiftKey &&
              !e.altKey &&
              !e.metaKey &&
              !e.ctrlKey
            ) {
              // Avoid intercepting during IME composition
              if (isComposing) return
              e.preventDefault()
              void doSubmit()
            }
            // Allow Cmd/Ctrl+Enter to send as well
            else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              if (isComposing) return
              e.preventDefault()
              void doSubmit()
            }
          }}
          className={cx("h-32 w-full resize-y rounded-xl", textAreaClassName)}
        />
      </div>

      <div className="flex w-full items-center justify-end gap-3 px-3 py-2">
        <Button
          size="sm"
          color="link-gray"
          iconLeading={Flash}
          className="text-xs font-semibold"
          type="button"
          onClick={onHelpRequested}
          isDisabled={isSending}
        >
          Help me word this
        </Button>

        <Button
          type="submit"
          size="sm"
          color="secondary"
          iconLeading={Send01}
          isDisabled={isSending || !value.trim()}
          isLoading={isSending}
          aria-label="Send"
        />
      </div>
    </form>
  )
}
